import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefuelHistoryModal from 'components/molecules/RefuelHistoryModal';
import HeaderAndSidebar from 'components/templates/HeaderAndSidebar';
import { range, unixTimeToString } from 'libs';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  RefuelHistory,
  RefuelHistoryRequestParam,
  Vehicle,
  VehicleKind,
} from 'types';

type Props = {
  isLoading: boolean;
  vehicle: Vehicle;
  histories: RefuelHistory[];
  onClickUpdateVehicle: (vehicle: Vehicle) => void;
  onClickCreateHistory: (history: RefuelHistoryRequestParam) => void;
  onClickUpdateHistory: (history: RefuelHistoryRequestParam) => void;
  onClickDeleteHistory: (history: RefuelHistory) => void;
};

const initialSelectedHistory: RefuelHistory = {
  id: -1,
  refuel_at: 0,
  mileage: 0,
  fuel_consumption: 0,
  refuel_amount: 0,
};

const RefuelHistories: React.FC<Props> = ({
  isLoading,
  vehicle,
  histories,
  onClickUpdateVehicle,
  onClickCreateHistory,
  onClickUpdateHistory,
  onClickDeleteHistory,
}) => {
  const [vehicleName, setVehicleName] = useState(vehicle.name);
  const [vehicleKind, setVehicleKind] = useState<VehicleKind>(vehicle.kind);
  const [vehicleTotalMileage, setVehicleTotalMileage] = useState(
    vehicle.total_mileage,
  );
  const [files, setFiles] = useState<FileList | null>(null);

  const [historyYear, setHistoryYear] = useState('');
  const [historyMonth, setHistoryMonth] = useState('');
  const [historyDay, setHistoryDay] = useState('');
  const [historyMileage, setHistoryMileage] = useState(0);
  const [historyTotalMileage, setHistoryTotalMileage] = useState(0);
  const [historyFuelPrice, setHistoryFuelPrice] = useState(0);
  const [historyRefuelAmount, setHistoryRefuelAmount] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<RefuelHistory>(
    initialSelectedHistory,
  );

  useEffect(() => {
    setVehicleName(vehicle.name);
    setVehicleKind(vehicle.kind);
    setVehicleTotalMileage(vehicle.total_mileage);
  }, [vehicle]);

  const onClickVehicle = () => {
    if (!files) {
      const updatedVehicle: Vehicle = {
        ...vehicle,
        name: vehicleName,
        kind: vehicleKind,
        total_mileage: vehicleTotalMileage,
      };
      onClickUpdateVehicle(updatedVehicle);

      return;
    }

    const file = files[0];
    if (!file) {
      const updatedVehicle: Vehicle = {
        ...vehicle,
        name: vehicleName,
        kind: vehicleKind,
        total_mileage: vehicleTotalMileage,
      };
      onClickUpdateVehicle(updatedVehicle);

      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      if (!fileReader.result) return;
      if (typeof fileReader.result !== 'string') return;

      const updatedVehicle: Vehicle = {
        ...vehicle,
        name: vehicleName,
        kind: vehicleKind,
        image: fileReader.result,
        total_mileage: vehicleTotalMileage,
      };
      onClickUpdateVehicle(updatedVehicle);
    };
    fileReader.readAsDataURL(file);
  };

  const onClickCreate = () => {
    if (historyDay === '' || historyMonth === '' || historyDay === '') return;

    const refuelAt =
      new Date(
        parseInt(historyYear, 10),
        parseInt(historyMonth, 10) - 1,
        parseInt(historyDay, 10),
        9,
      ).getTime() / 1000;

    const newHistory: RefuelHistoryRequestParam = {
      id: -1,
      refuel_at: refuelAt,
      mileage: historyMileage,
      total_mileage: historyTotalMileage,
      refuel_amount: historyRefuelAmount,
      fuel_price: historyFuelPrice,
    };

    onClickCreateHistory(newHistory);
  };

  return (
    <HeaderAndSidebar pageTitle="燃費計算">
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Typography component="h2" variant="h6" gutterBottom>
                {vehicle.name}
              </Typography>
              <Grid container spacing={1}>
                <Grid item>
                  <img src={vehicle.image || ''} alt="vehicle" width={250} />
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Box component="form" noValidate sx={{ mt: 1 }}>
              <Stack spacing={3} maxWidth={400}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setFiles(event.target.files)}
                />
                <TextField
                  margin="normal"
                  required
                  label="名前"
                  name="name"
                  onChange={(event) => {
                    setVehicleName(event.target.value);
                  }}
                  value={vehicleName}
                />
                <Select
                  label="種別"
                  name="kind"
                  autoWidth
                  onChange={(event) => {
                    setVehicleKind(event.target.value as VehicleKind);
                  }}
                  value={vehicleKind}
                >
                  <MenuItem value="normal_car">普通自動車</MenuItem>
                  <MenuItem value="motorcycle50">原付</MenuItem>
                </Select>
                <TextField
                  margin="normal"
                  required
                  type="number"
                  label="総走行距離"
                  name="total_mileage"
                  inputProps={{ min: 0, step: 0.1 }}
                  onChange={(event) => {
                    setVehicleTotalMileage(parseFloat(event.target.value));
                  }}
                  value={vehicleTotalMileage}
                />
                <Button
                  type="button"
                  variant="contained"
                  sx={{ maxWidth: '64px' }}
                  onClick={onClickVehicle}
                >
                  更新
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <Stack spacing={3} maxWidth={400}>
              <Grid container>
                <Select
                  label="年"
                  name="year"
                  autoWidth
                  onChange={(event) => {
                    setHistoryYear(event.target.value);
                  }}
                  value={historyYear}
                >
                  {[2024, 2025, 2026].map((y, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MenuItem key={index} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  label="月"
                  name="month"
                  autoWidth
                  onChange={(event) => {
                    setHistoryMonth(event.target.value);
                  }}
                  value={historyMonth}
                >
                  {range(1, 13).map((m, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MenuItem key={index} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  label="日"
                  name="day"
                  autoWidth
                  onChange={(event) => {
                    setHistoryDay(event.target.value);
                  }}
                  value={historyDay}
                >
                  {range(1, 32).map((d, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MenuItem key={index} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <TextField
                margin="normal"
                required
                type="number"
                label="走行距離(km)"
                name="mileage"
                inputProps={{ min: 0, step: 0.1 }}
                onChange={(event) => {
                  setHistoryMileage(parseFloat(event.target.value));
                }}
                value={historyMileage}
              />
              <TextField
                margin="normal"
                required
                type="number"
                label="総走行距離(km)"
                name="total_mileage"
                inputProps={{ min: 0, step: 0.1 }}
                onChange={(event) => {
                  setHistoryTotalMileage(parseFloat(event.target.value));
                }}
                value={historyTotalMileage}
              />
              <TextField
                margin="normal"
                required
                type="number"
                label="給油量(l)"
                name="refuel_amount"
                inputProps={{ min: 0, step: 0.1 }}
                onChange={(event) => {
                  setHistoryRefuelAmount(parseFloat(event.target.value));
                }}
                value={historyRefuelAmount}
              />
              <TextField
                margin="normal"
                required
                type="number"
                label="燃料単価"
                name="fuel_price"
                inputProps={{ min: 0 }}
                onChange={(event) => {
                  setHistoryFuelPrice(parseInt(event.target.value, 10));
                }}
                value={historyFuelPrice}
              />
              <Button
                variant="contained"
                sx={{ maxWidth: '64px' }}
                onClick={onClickCreate}
              >
                追加
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            給油履歴
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Table stickyHeader sx={{ maxHeight: 440 }}>
              <TableHead>
                <TableRow>
                  <TableCell>月</TableCell>
                  <TableCell>給油量</TableCell>
                  <TableCell>走行距離</TableCell>
                  <TableCell>燃費</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {histories
                  .sort((a, b) => a.refuel_at - b.refuel_at)
                  .map((history) => (
                    <TableRow key={history.id}>
                      <TableCell>
                        {unixTimeToString(history.refuel_at)}
                      </TableCell>
                      <TableCell>
                        {history.refuel_amount.toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        {history.mileage.toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        {history.fuel_consumption?.toLocaleString('ja-JP') ||
                          ''}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setSelectedHistory(history);
                            setModalOpen(true);
                          }}
                        >
                          編集
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => onClickDeleteHistory(history)}
                        >
                          削除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={12} lg={12}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Line
              data={{
                labels: histories.map((history) =>
                  unixTimeToString(history.refuel_at),
                ),
                datasets: [
                  {
                    data: histories.map((history) => history.fuel_consumption),
                    backgroundColor: '#007bff',
                    borderColor: '#007bff',
                    label: '燃費',
                  },
                ],
              }}
            />
          )}
        </Paper>
      </Grid>
      <RefuelHistoryModal
        open={modalOpen}
        vehicle={vehicle}
        history={selectedHistory}
        onClose={() => setModalOpen(false)}
        onClickUpdateHistory={(history) => {
          setModalOpen(false);
          onClickUpdateHistory(history);
        }}
      />
    </HeaderAndSidebar>
  );
};

export default RefuelHistories;
